import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { CategoryNode } from '../api/types';
import { cn } from '../lib/utils';
import { isInstitutionLevel, isTemplatePlaceholder, placeholderDisplayName } from '../lib/period-tree';
import { Icon } from './icons';

interface FlatCategory {
  id: string;
  name: string;
  depth: number;
  /** The row's own name, lower-cased (search). */
  nameLower: string;
  /** Every name from the top level down to this row, lower-cased and
   * ` / `-joined (search across the path, e.g. "chase 4321"). */
  pathLower: string;
  /** The same path as people read it (announced for the current match). */
  path: string;
  /** A heading only — never a place to file (the "[Institution]" level). */
  heading?: boolean;
}

/** The synthetic "Uncategorized" bucket is a view artifact, not a real tag, so
 * it is never offered as a selectable category. */
const SYNTHETIC_IDS = new Set(['uncategorized', 'demo-cat-uncat']);

/** Flatten the category tree to depth-tagged rows, skipping the synthetic
 * "Uncategorized" node (and its subtree, though it has none). */
function flatten(nodes: CategoryNode[], depth = 0, parentPath: string[] = []): FlatCategory[] {
  const out: FlatCategory[] = [];
  for (const node of nodes) {
    if (SYNTHETIC_IDS.has(node.id)) continue;
    // A placeholder ("[Account Name]", "[Credit Card]") is not a folder: picking
    // it files the document at its parent and makes the account (or state) from
    // the document itself (the server does it, owner rule 2026-10-12). The
    // account tree's "[Institution]" level is a heading, "Bank not identified",
    // over the accounts whose bank is not known yet — they are picked, it is not
    // (the server refuses it, review 2026-10-15).
    const heading = isInstitutionLevel(node.name);
    const name = heading
      ? placeholderDisplayName(node.name)
      : isTemplatePlaceholder(node.name)
        ? 'Not listed — make it from this document'
        : node.name;
    const names = [...parentPath, name];
    const path = names.join(' / ');
    out.push({
      id: node.id,
      name,
      depth,
      nameLower: name.toLowerCase(),
      pathLower: path.toLowerCase(),
      path,
      ...(heading ? { heading: true } : {}),
    });
    out.push(...flatten(node.children, depth + 1, names));
  }
  return out;
}

/** How long typing pauses before the tree is searched. */
const SEARCH_DEBOUNCE_MS = 150;

/** The search's words: lower-cased, split on spaces and path separators. */
function searchTerms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[\s/›>]+/)
    .filter((t) => t.length > 0);
}

/**
 * A row matches when every word appears somewhere on its path (any part of any
 * name, so "chase 4321" finds the 4321 account under Chase) AND at least one
 * word is in the row's own name — so "chase" highlights the Chase branch, not
 * every row beneath it.
 */
function isMatch(opt: FlatCategory, terms: readonly string[]): boolean {
  return (
    terms.length > 0 &&
    terms.every((t) => opt.pathLower.includes(t)) &&
    terms.some((t) => opt.nameLower.includes(t))
  );
}

/** The row's name with every occurrence of a search word marked. */
function markTerms(name: string, nameLower: string, terms: readonly string[]): ReactNode {
  // Lower-casing changed the length (rare Unicode): show the name unmarked
  // rather than mark the wrong letters.
  if (nameLower.length !== name.length) return name;
  const ranges: [number, number][] = [];
  for (const t of terms) {
    for (let at = nameLower.indexOf(t); at !== -1; at = nameLower.indexOf(t, at + 1)) {
      ranges.push([at, at + t.length]);
    }
  }
  if (ranges.length === 0) return name;
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  const parts: ReactNode[] = [];
  let pos = 0;
  merged.forEach(([from, to], i) => {
    if (from > pos) parts.push(name.slice(pos, from));
    parts.push(
      <mark key={i} className="rounded-sm bg-brass-soft px-px text-ink">
        {name.slice(from, to)}
      </mark>,
    );
    pos = to;
  });
  if (pos < name.length) parts.push(name.slice(pos));
  return parts;
}

/**
 * Scroll `row` to the middle of the list's own scroll box, so a match shows
 * with its neighbours around it and the dialog behind does not jump. Outside a
 * scroll box, the browser's nearest-edge scroll.
 */
function reveal(row: HTMLElement) {
  let box = row.parentElement;
  while (
    box &&
    !(box.scrollHeight > box.clientHeight && /auto|scroll/.test(getComputedStyle(box).overflowY))
  ) {
    box = box.parentElement;
  }
  if (!box || box === document.documentElement || box === document.body) {
    row.scrollIntoView?.({ block: 'nearest' });
    return;
  }
  const offset = row.getBoundingClientRect().top - box.getBoundingClientRect().top;
  box.scrollTop += offset - (box.clientHeight - row.offsetHeight) / 2;
}

interface CategoryPickerProps {
  /** The room's category hierarchy (the selectable category tags). */
  categories: CategoryNode[];
  /** The working selection of category tag ids. */
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  disabled?: boolean;
}

/**
 * The indented category-tag checklist (PRD-0004 §3i). ONE picker shared by
 * "Modify document attributes", "Choose a different branch" in the
 * classification review panel and "Add a new category" (where it goes inside),
 * so the admin picks a category the same way everywhere. Presentational: the
 * caller owns the selection and the save.
 *
 * Search (owner ask 2026-10-15): one box above the tree finds a branch IN the
 * tree — matching rows are marked in place, the rest stay visible but muted,
 * and the first match scrolls into view. No separate results list and no
 * filtered view. Every level is always shown, so a match's parents are always
 * visible around it. Enter / ↓ and Shift+Enter / ↑ (or the arrow buttons) step
 * through the matches; Esc or ✕ clears. While a search has matches, Tab from
 * the box lands on the matches only; selecting one works exactly as without a
 * search.
 */
export function CategoryPicker({ categories, selected, onToggle, disabled }: CategoryPickerProps) {
  const options = useMemo(() => flatten(categories), [categories]);
  const inputId = useId();
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const rows = useRef(new Map<string, HTMLLIElement>());

  // `query` is what is typed; `applied` is what the tree shows (debounced).
  const [query, setQuery] = useState('');
  const [applied, setApplied] = useState('');
  const [current, setCurrent] = useState(0);
  // Bumped on every step so stepping onto the same row still scrolls to it.
  const [step, setStep] = useState(0);

  // Show `q` in the tree now, from its first match.
  const apply = (q: string) => {
    setApplied(q);
    setCurrent(0);
    setStep((s) => s + 1);
  };

  useEffect(() => {
    if (query === applied) return;
    const timer = setTimeout(() => {
      setApplied(query);
      setCurrent(0);
      setStep((s) => s + 1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, applied]);

  const terms = useMemo(() => searchTerms(applied), [applied]);
  const matchIds = useMemo(
    () => options.filter((o) => isMatch(o, terms)).map((o) => o.id),
    [options, terms],
  );
  const matchSet = useMemo(() => new Set(matchIds), [matchIds]);
  const searching = terms.length > 0;
  const total = matchIds.length;
  const at = total > 0 ? Math.min(current, total - 1) : 0;
  const currentId = total > 0 ? matchIds[at] : null;

  useEffect(() => {
    const row = currentId ? rows.current.get(currentId) : undefined;
    if (row) reveal(row);
  }, [currentId, step]);

  const move = (by: 1 | -1) => {
    // Enter before the pause: search now, and land on the first match.
    if (query !== applied) return apply(query);
    if (total === 0) return;
    setCurrent((at + by + total) % total);
    setStep((s) => s + 1);
  };

  const clear = () => {
    setQuery('');
    apply('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Never submit a surrounding form (Add a new category).
      e.preventDefault();
      move(e.shiftKey ? -1 : 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Escape' && query !== '') {
      // Clear the search; the dialog's own Escape (close) is left for the next press.
      e.preventDefault();
      e.stopPropagation();
      clear();
    }
  };

  if (options.length === 0) {
    return <p className="text-sm text-muted-foreground">No categories in this room yet.</p>;
  }

  const status = !searching
    ? ''
    : total === 0
      ? 'No matches'
      : total === 1
        ? '1 match'
        : `${at + 1} of ${total} matches`;
  const currentPath = currentId ? options.find((o) => o.id === currentId)?.path : undefined;

  return (
    <div>
      <div className="sticky top-0 z-10 bg-card pb-2">
        <div className="flex items-center gap-1.5">
          <div className="relative min-w-0 flex-1">
            <label htmlFor={inputId} className="sr-only">
              Find a category
            </label>
            <Icon
              name="search"
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3"
            />
            <input
              ref={inputRef}
              id={inputId}
              type="search"
              autoComplete="off"
              spellCheck={false}
              placeholder="Find a category — e.g. chase 4321"
              aria-describedby={hintId}
              aria-controls={`${inputId}-tree`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-8 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
            />
            {query !== '' ? (
              <button
                type="button"
                aria-label="Clear search"
                title="Clear search (Esc)"
                onClick={() => {
                  clear();
                  inputRef.current?.focus();
                }}
                className="absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-ink-3 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Icon name="close" size={14} />
              </button>
            ) : null}
          </div>
          {searching ? (
            <>
              <span
                className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground"
                aria-live="polite"
                aria-atomic="true"
                data-testid="search-status"
              >
                {status}
                {currentPath ? <span className="sr-only">: {currentPath}</span> : null}
              </span>
              <button
                type="button"
                aria-label="Previous match"
                title="Previous match (Shift+Enter or ↑)"
                disabled={total < 2}
                onClick={() => move(-1)}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-ink-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
              >
                <Icon name="chevronDown" size={14} className="rotate-180" />
              </button>
              <button
                type="button"
                aria-label="Next match"
                title="Next match (Enter or ↓)"
                disabled={total < 2}
                onClick={() => move(1)}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-ink-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
              >
                <Icon name="chevronDown" size={14} />
              </button>
            </>
          ) : null}
        </div>
        <p id={hintId} className="sr-only">
          Matching categories are highlighted in the list below. Enter or down arrow for the next
          match, Shift+Enter or up arrow for the previous one, Escape to clear.
        </p>
      </div>
      <ul id={`${inputId}-tree`} className="space-y-1">
        {options.map((opt) => {
          const match = searching && matchSet.has(opt.id);
          const isCurrent = opt.id === currentId;
          // While a search has matches, the rest step back (still there, for
          // context) and leave the tab order to the matches.
          const muted = searching && total > 0 && !match;
          return (
            <li
              key={opt.id}
              ref={(el) => {
                if (el) rows.current.set(opt.id, el);
                else rows.current.delete(opt.id);
              }}
              data-match={match ? (isCurrent ? 'current' : 'true') : undefined}
              aria-current={isCurrent ? 'true' : undefined}
              style={{ scrollMarginTop: 56 }}
            >
              <label
                className={cn(
                  'flex items-center gap-2 rounded px-2 py-1 text-sm',
                  !opt.heading && 'cursor-pointer hover:bg-accent',
                  muted && 'text-muted-foreground',
                  isCurrent && 'bg-primary-soft ring-1 ring-primary hover:bg-primary-soft',
                )}
                style={{ paddingLeft: opt.depth * 14 + 8 }}
              >
                {opt.heading ? (
                  <span className="h-4 w-4" aria-hidden="true" />
                ) : (
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={selected.has(opt.id)}
                  disabled={disabled}
                  tabIndex={muted ? -1 : undefined}
                  onFocus={() => {
                    if (match && !isCurrent) setCurrent(matchIds.indexOf(opt.id));
                  }}
                  onChange={() => onToggle(opt.id)}
                />
                )}
                <span className={cn('flex-1', opt.heading && 'text-muted-foreground')}>
                  {match ? markTerms(opt.name, opt.nameLower, terms) : opt.name}
                  {match ? (
                    <span className="sr-only">
                      {isCurrent ? ' (current search match)' : ' (search match)'}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
